import { useId, useState } from 'react';
import { doctorPriceRefusal } from '@/domain';
import type { DoctorItemPrice } from '@/application/ports/FinanceRepository';
import { useClearDoctorPrice, useDoctorPrices, useSetDoctorPrice } from '@/application/usecases/useFinance';
import { formatDay } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { useMoney } from '@/ui/money/moneyView';
import { toast } from '@/ui/primitives/toastStore';
import { useConfirmAction } from '../shell/useConfirmAction';
import { parseAmount } from './moneyForm';
import styles from './Finance.module.css';
import own from './DoctorPrices.module.css';

const t = strings.doctorPrices;

/**
 * A doctor's own prices. The lab's request of 17 October 2026, item 11: "a
 * general items price list which applies to everyone, and then accountant can
 * open each doctors page and edit its item prices. If the price changed, in
 * the past cases the prices should stay the same, and only in the future cases
 * the price should change."
 *
 * Every item of the price list with its price for everyone and the doctor's
 * own beside it, to type over. Save asks first and says that only the doctor's
 * next cases take it; Use list price puts the item back to what everybody
 * pays. Drawn for a holder of pricing, on the doctor's statement and on the
 * doctor's profile.
 */
export function DoctorPrices({ doctorId, doctorName }: { doctorId: string; doctorName: string }) {
  const prices = useDoctorPrices(doctorId);
  const headingId = useId();

  return (
    <section className={`${styles.card} ${styles.gap}`} aria-labelledby={headingId}>
      <div className={styles.section}>
        <h3 id={headingId}>{t.heading}</h3>
      </div>
      <p className={styles.hint}>{t.lead}</p>

      {prices.isPending && <SkeletonRows rows={4} label={t.loading} />}
      {prices.isError && <p className={styles.warn}>{strings.errors.server}</p>}
      {prices.data !== undefined &&
        (prices.data.items.length === 0 ? (
          <p className={styles.empty}>{t.empty}</p>
        ) : (
          <ul className={styles.rows} aria-label={t.listLabel(doctorName)}>
            {prices.data.items.map((item) => (
              <PriceRow key={item.itemId} doctorId={doctorId} doctorName={doctorName} item={item} />
            ))}
          </ul>
        ))}
    </section>
  );
}

function PriceRow({
  doctorId,
  doctorName,
  item,
}: {
  doctorId: string;
  doctorName: string;
  item: DoctorItemPrice;
}) {
  const set = useSetDoctorPrice();
  const clear = useClearDoctorPrice();
  const ask = useConfirmAction();
  const money = useMoney();
  const inputId = useId();
  const [typed, setTyped] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const shown = typed ?? (item.doctorPrice === null ? '' : String(item.doctorPrice));
  const changed = typed !== null && typed.trim() !== '' && parseAmount(typed) !== item.doctorPrice;

  function save() {
    const price = typed === null ? null : Number(typed);
    const refusal = doctorPriceRefusal(price === null || Number.isNaN(price) ? null : price);
    if (refusal !== null || price === null) {
      setProblem(refusal ?? t.badPrice);
      return;
    }
    setProblem(null);
    ask(
      strings.confirm.setDoctorPrice(item.name, doctorName, money(price)),
      () =>
        set.mutateAsync({ doctorId, itemId: item.itemId, unitPrice: price }).then(() => {
          setTyped(null);
          toast(t.saved(item.name, money(price)), { tone: 'success' });
        }),
      { confirmLabel: t.save },
    );
  }

  function backToList() {
    ask(
      strings.confirm.clearDoctorPrice(item.name, doctorName, money(item.listPrice)),
      () =>
        clear.mutateAsync({ doctorId, itemId: item.itemId }).then(() => {
          setTyped(null);
          toast(t.cleared(item.name), { tone: 'success' });
        }),
      { confirmLabel: t.useList },
    );
  }

  return (
    <li className={styles.row}>
      <div className={styles.what}>
        <b>
          {item.name}
          {!item.active && <span className={own.retired}> · {t.retired}</span>}
        </b>
        <span>
          {item.category} · {t.listPrice(money(item.listPrice))}
          {item.doctorPrice !== null && item.updatedAt !== null && ` · ${t.setOn(formatDay(item.updatedAt))}`}
        </span>
      </div>
      <div className={own.price}>
        <label className={own.label} htmlFor={inputId}>
          {t.theirPrice}
        </label>
        <input
          id={inputId}
          className={own.input}
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          placeholder={String(item.listPrice)}
          value={shown}
          aria-invalid={problem !== null}
          onChange={(event) => {
            setTyped(event.target.value);
            setProblem(null);
          }}
        />
      </div>
      <div className={styles.rowActions}>
        {changed && (
          <button type="button" className={styles.mini} disabled={set.isPending} onClick={save}>
            {t.save}
          </button>
        )}
        {item.doctorPrice !== null && !changed && (
          <button type="button" className={styles.mini} disabled={clear.isPending} onClick={backToList}>
            {t.useList}
          </button>
        )}
      </div>
      {problem !== null && (
        <p className={own.problem} role="alert">
          {problem}
        </p>
      )}
    </li>
  );
}
